/**
 * Turning browser `File`s picked in the OS dialog into the descriptors the
 * upload takeover renders. Everything here is best-effort and degrades to
 * "unknown" rather than guessing: an undefined `pages` simply hides the page
 * indicator, it never shows a made-up count.
 */
import {
  MULTIPART_THRESHOLD_BYTES,
  MULTIPART_THRESHOLD_LABEL,
} from "#helpers/upload-multipart";
import type { SkippedFiles, UploadFileDescriptor } from "./types";

/** Hard upload ceiling. Files above this can't be filed and must be removed. */
export const MAX_UPLOAD_BYTES = 1024 ** 3; // 1 GB

/** Human label for the limit, for use in copy. */
export const MAX_UPLOAD_LABEL = "1 GB";

/**
 * Whether a descriptor has zero bytes. Mirrors the backend create rule
 * (`File size must be greater than zero`) so empty files never hit the network.
 */
export function isEmptyFile(file: { bytes?: number; source?: File }): boolean {
  const bytes = file.bytes ?? file.source?.size;
  return bytes === 0;
}

/** How many documents can be filed in a single upload. */
export const MAX_UPLOAD_FILES = 10;

/**
 * What to tell the user when the picker drops files over the cap. Lives next to the
 * cap so the copy can't quietly start lying when the number changes — it read
 * "up to 10" with the 10 hardcoded in the sentence.
 */
export const exceedLimitMessage = (dropped: number): string =>
  `You can upload up to ${MAX_UPLOAD_FILES} documents at once — the last ${dropped} ` +
  `${dropped === 1 ? "was" : "were"} skipped.`;

/**
 * Whether a descriptor exceeds the upload limit. Needs the raw byte count, so a
 * descriptor built by hand with only a formatted `size` string reads as fine —
 * `describeFiles` always records `bytes`.
 */
export function isOversize(file: { bytes?: number; source?: File }): boolean {
  const bytes = file.bytes ?? file.source?.size;
  return bytes != null && bytes > MAX_UPLOAD_BYTES;
}

/**
 * Ceiling for a file travelling in a MULTI-file batch. `createVaultDocumentsBatch`
 * mints single-part upload targets only, so anything DSS would put on the multipart
 * path can't ride along — it has to go up on its own, where the limit is
 * {@link MAX_UPLOAD_BYTES}.
 */
export const MAX_BATCH_UPLOAD_BYTES = MULTIPART_THRESHOLD_BYTES;

/**
 * Whether a descriptor is too large to travel in a multi-file batch. `>=` matches
 * where DSS actually switches to multipart, so the boundary file is rejected here
 * rather than by the storage layer. Unknown size reads as allowed, like
 * {@link isOversize} — guessing would block a legitimate file.
 */
export function isTooLargeForBatch(file: { bytes?: number; source?: File }): boolean {
  const bytes = file.bytes ?? file.source?.size;
  return bytes != null && bytes >= MAX_BATCH_UPLOAD_BYTES;
}

/**
 * One sentence covering everything the picker dropped. Lives next to the caps for
 * the same reason as {@link exceedLimitMessage} — so the numbers in the copy can't
 * drift from the numbers being enforced.
 *
 * Both reasons have to compose into a single string: the callers show it in a
 * one-slot toast, so reporting them separately lost whichever landed first.
 * Count-only returns {@link exceedLimitMessage} verbatim, keeping the shipped
 * wording for the case that already existed. Oversize files are named when
 * there's one and counted when there are several, rather than listing ten.
 *
 * Returns null when nothing was dropped, so callers can skip the toast entirely.
 */
export const skippedFilesMessage = ({
  droppedForCount,
  oversizeNames,
}: SkippedFiles): string | null => {
  if (droppedForCount === 0 && oversizeNames.length === 0) return null;
  if (oversizeNames.length === 0) return exceedLimitMessage(droppedForCount);

  const one = oversizeNames.length === 1;
  const subject = one ? `${oversizeNames[0]} is` : `${oversizeNames.length} files are`;
  const action = one ? "add it on its own" : "add them one at a time";
  const oversize =
    `${subject} over ${MULTIPART_THRESHOLD_LABEL} and can't be part of a ` +
    `multi-file upload — ${action}.`;
  if (droppedForCount === 0) return oversize;

  return (
    `${oversize} Another ${droppedForCount} ${droppedForCount === 1 ? "was" : "were"} ` +
    `skipped to keep the selection within ${MAX_UPLOAD_FILES} documents.`
  );
};

/** Lower-cased extension without the dot ("" when the name carries none). */
export function extensionOf(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name.trim());
  return m ? m[1].toLowerCase() : "";
}

/** Byte count as a short human label, e.g. `145 KB` / `3.4 MB`. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/**
 * Page count read straight out of the PDF bytes — no parser dependency.
 * Prefers counting page objects; falls back to the page tree's `/Count` (which
 * is what survives when the objects live inside compressed object streams).
 * Returns undefined when neither is readable.
 */
export function pdfPageCount(bytes: Uint8Array): number | undefined {
  const text = new TextDecoder("latin1").decode(bytes);
  // `[^s]` keeps `/Type /Pages` (the tree node) out of the page-object count.
  const objects = text.match(/\/Type\s*\/Page[^s]/g);
  if (objects && objects.length > 0) return objects.length;
  const counts = Array.from(text.matchAll(/\/Count\s+(\d+)/g), (m) => Number(m[1]));
  const max = counts.length > 0 ? Math.max(...counts) : 0;
  return max > 0 ? max : undefined;
}

/** Above this we skip the page scan rather than pull the whole file into memory. */
const PAGE_SCAN_LIMIT_BYTES = 25 * 1024 * 1024;

async function countPagesOf(file: File): Promise<number | undefined> {
  if (extensionOf(file.name) !== "pdf" || file.size > PAGE_SCAN_LIMIT_BYTES) return undefined;
  try {
    return pdfPageCount(new Uint8Array(await file.arrayBuffer()));
  } catch {
    return undefined;
  }
}

/** Monotonic within the page session — enough to key a picked file uniquely. */
let idSeq = 0;
const nextId = () => {
  idSeq += 1;
  return `upload-${idSeq}`;
};

/** Describe picked files for the takeover, keeping the original `File` for preview/download. */
export async function describeFiles(files: File[]): Promise<UploadFileDescriptor[]> {
  return Promise.all(
    files.map(async (source) => ({
      id: nextId(),
      name: source.name,
      size: formatFileSize(source.size),
      bytes: source.size,
      // An oversize file is going to be removed, so don't read it just to count pages.
      pages: source.size > MAX_UPLOAD_BYTES ? undefined : await countPagesOf(source),
      source,
    })),
  );
}
